import React, { useEffect, useMemo, useState } from 'react';
import {
    Upload,
    Table,
    Checkbox,
    Select,
    InputNumber,
    Button,
    Space,
    Tag,
    Progress,
    Typography,
    message,
    Alert,
} from 'antd';
import type { UploadProps } from 'antd';
import { InboxOutlined, PlayCircleOutlined, ClearOutlined } from '@ant-design/icons';
import { Upload as TusUpload } from 'tus-js-client';

type Project = {
    id: number;
    name: string;
    organizationSlug?: string;
    organizationName?: string;
};

type Organization = { id: number; slug: string; name: string };

type Group = {
    raw: File[];
    label16Zip?: File;
    label16Files: File[];
};

type Row = {
    key: string;
    taskName: string;
    rawCount: number;
    hasLabelZip: boolean;
    labelCount: number;
    status: 'queued' | 'creating' | 'uploading' | 'importing' | 'done' | 'error' | 'error_duplicate' | 'skipped' | 'waiting';
    note?: string;
    taskId?: number;
    progress?: number;
};

const ANNOTATION_FORMAT = 'label16 1.0';

function groupFiles(files: File[]): Record<string, Group> {
    const grouped: Record<string, Group> = {};

    for (const fAny of files as any as { webkitRelativePath?: string; name: string }[]) {
        const f = fAny as File & { webkitRelativePath?: string };

        // Normalize path and split into segments
        const relRaw = f.webkitRelativePath || f.name;
        if (!relRaw) continue;
        const rel = relRaw.replace(/\\/g, '/'); // just in case (Windows)
        const parts = rel.split('/').filter(Boolean);
        const lower = parts.map((p) => p.toLowerCase());

        // Find the task folder as the parent of `raw` or `label16` anywhere in the path
        const rawIdx = lower.indexOf('raw');
        const l16Idx = lower.indexOf('label16');

        let taskName: string | undefined;
        let kind: 'raw' | 'label16' | 'label16Zip' | undefined;

        if (rawIdx > 0) {
            taskName = parts[rawIdx - 1];
            kind = 'raw';
        } else if (l16Idx > 0) {
            taskName = parts[l16Idx - 1];
            // If a zip is directly under label16/, treat as the zip variant
            const basename = parts[parts.length - 1].toLowerCase();
            kind = basename.endsWith('.zip') ? 'label16Zip' : 'label16';
        } else if (
            // Support Task/label16.zip pattern (no label16/ folder)
            parts.length >= 2 &&
            parts[parts.length - 1].toLowerCase() === 'label16.zip'
        ) {
            taskName = parts[parts.length - 2];
            kind = 'label16Zip';
        } else {
            // Not under raw/ or label16/, ignore (bbox/, label/, line/, json, etc.)
            continue;
        }

        if (!taskName) continue;
        if (!grouped[taskName]) grouped[taskName] = { raw: [], label16Files: [] };

        if (kind === 'raw') {
            grouped[taskName].raw.push(f);
        } else if (kind === 'label16Zip') {
            grouped[taskName].label16Zip = f;
        } else {
            grouped[taskName].label16Files.push(f);
        }
    }

    return grouped;
}

function getCSRFToken(): string | null {
    return (
        document.cookie
            .split('; ')
            .find((row) => row.startsWith('csrftoken='))
            ?.split('=')[1] || null
    );
}

function buildHeaders(extra?: Record<string, string>) {
    const h: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
    const csrf = getCSRFToken();
    if (csrf) h['X-CSRFToken'] = csrf;
    if (extra) Object.assign(h, extra);
    return h;
}

function withOrg(url: string, org?: string) {
    return org ? `${url}${url.includes('?') ? '&' : '?'}org=${encodeURIComponent(org)}` : url;
}

function appendQS(url: string, qs: string) {
    return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
}

async function zipLabel16(files: File[], onZipProgress?: (pct: number) => void): Promise<File> {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    files.forEach((f: any) => {
        const rel = f.webkitRelativePath || f.name;
        const parts = rel.split('/');
        const idx = parts.findIndex((p: string) => p.toLowerCase() === 'label16');
        const inside = idx >= 0 ? parts.slice(idx + 1).join('/') : f.name;
        zip.file(inside || f.name, f);
    });
    const blob = await zip.generateAsync(
        { type: 'blob', compression: 'STORE' }, // no extra CPU for PNGs
        (meta) => onZipProgress?.(Math.round(meta.percent)),
    );

    return new File([blob], 'label16.zip', { type: 'application/zip' });
}

export default function BulkTaskLoaderPage(): JSX.Element {
    const [existingTasks, setExistingTasks] = useState<Set<string>>(new Set());
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectId, setProjectId] = useState<number>();
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [files, setFiles] = useState<File[]>([]);
    const [imageQuality, setImageQuality] = useState<number>(100);
    const [concurrency, setConcurrency] = useState<number>(2);
    const [rows, setRows] = useState<Row[]>([]);
    const [running, setRunning] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [orgSlug, setOrgSlug] = useState<string | undefined>(undefined);
    useEffect(() => {
        try {
            const raw = localStorage.getItem('currentOrganization');
            const obj = raw ? JSON.parse(raw) : null;
            if (obj?.slug) setOrgSlug(obj.slug);
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(withOrg('/api/projects?page_size=1000', orgSlug), { credentials: 'include' });
                const data = await res.json();

                const byId = new Map(orgs.map((o) => [o.id, o]));
                const list: Project[] = (data.results || data).map((p: any) => {
                    let slug: string | undefined;
                    let name: string | undefined;

                    // p.organization can be number (id), object, or null
                    if (p?.organization == null) {
                        slug = undefined; // Personal workspace
                        name = 'Personal';
                    } else if (typeof p.organization === 'number') {
                        const o = byId.get(p.organization);
                        slug = o?.slug;
                        name = o?.name;
                    } else {
                        slug = p.organization?.slug ?? undefined;
                        name = p.organization?.name ?? (slug ? slug : undefined);
                    }

                    // Fallback: if we are scoped to an org view and server didn’t include it
                    if (!slug && orgSlug) slug = orgSlug;

                    return {
                        id: p.id,
                        name: p.name,
                        organizationSlug: slug,
                        organizationName: name ?? 'Personal',
                    };
                });

                setProjects(list);
            } catch (e: any) {
                setLoadError(`Failed to load projects: ${e?.message || e}`);
            }
        })();
    }, [orgSlug, orgs]);

    const grouped = useMemo(() => groupFiles(files), [files]);
    const selectedProject = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
    const selectedOrg = selectedProject?.organizationSlug;
    // Prefer the project's org if defined; otherwise fall back to the UI's current org
    const preferredOrg = (selectedOrg ?? orgSlug)?.replace(/\/+$/, '');

    useEffect(() => {
        setRows((prev) => {
            const prevMap = new Map(prev.map((r) => [r.key, r]));
            const next: Row[] = [];

            // 1 — Build next[]
            for (const [taskName, g] of Object.entries(grouped)) {
                const old = prevMap.get(taskName);

                const base = {
                    key: taskName,
                    taskName,
                    rawCount: g.raw.length,
                    hasLabelZip: !!g.label16Zip,
                    labelCount: g.label16Zip ? 1 : g.label16Files.length,
                };

                if (old) {
                    next.push({ ...old, ...base });
                } else {
                    next.push({
                        ...base,
                        status: g.raw.length ? 'queued' : 'skipped',
                        note: g.raw.length ? '' : 'No raw/ images',
                        progress: 0,
                    });
                }
            }

            // Detect duplicates inside the uploaded folder structure
            const seen = new Set<string>(); 
            for (const row of next) {
                const lower = row.taskName.toLowerCase();
                if (seen.has(lower)) {
                    row.status = 'error_duplicate';
                    row.note = 'Duplicate task name in folder structure';
                }
                seen.add(lower);
            }

            // Detect duplicates with existing CVAT tasks
            for (const row of next) {
                const lower = row.taskName.toLowerCase();
                if (existingTasks.has(lower)) {
                    row.status = 'error_duplicate';
                    row.note = 'A task with this name already exists in CVAT';
                }
            }

            return next;
        });
    }, [grouped, existingTasks]);


    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/organizations?page_size=1000', { credentials: 'include' });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                const list: Organization[] = (data.results || data).map((o: any) => ({
                    id: o.id,
                    slug: o.slug,
                    name: o.name,
                }));
                setOrgs(list);
            } catch {
                setOrgs([]);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/tasks?page_size=5000', { credentials: 'include' });
                const data = await res.json();

                const names = new Set(
                    (data.results || data).map((t: any) => t.name.trim().toLowerCase())
                );

                setExistingTasks(names);
            } catch (e) {
                console.warn('Failed to load existing tasks for duplicate check', e);
            }
        })();
    }, []);


    const createTask = async (name: string, projectId_: number, ctxOrg?: string): Promise<number> => {
        const headers: Record<string, string> = buildHeaders(ctxOrg ? { 'X-Organization': ctxOrg } : undefined);
        headers['Content-Type'] = 'application/json';

        const res = await fetch(withOrg('/api/tasks', ctxOrg), {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ name, project_id: projectId_ }),
        });

        if (!res.ok) {
            const txt = await res.text().catch(() => '');
            throw new Error(`Create task failed (${res.status}) ${txt}`);
        }
        const data = await res.json();
        return data.id;
    };

    async function createQCJobsForFinished() {
        // Only rows that reached "done" in the UI
        const finishedRows = rows.filter(
            (r) => (r.status === 'done' || r.status === 'error' || r.status === 'waiting') && r.taskId,
        );

        if (!finishedRows.length) {
            message.info('No finished tasks with annotations.');
            return;
        }

        for (const row of finishedRows) {
            try {
                // Check the task details
                const taskResp = await fetch(withOrg(`/api/tasks/${row.taskId}`, preferredOrg), {
                    credentials: 'include',
                    headers: buildHeaders(preferredOrg ? { 'X-Organization': preferredOrg } : undefined),
                });
                if (!taskResp.ok) throw new Error(await taskResp.text());
                const task = await taskResp.json();
                console.log('Task details:', task);
                // Ensure annotations exist
                const jobsResp = await fetch(withOrg(`/api/jobs?task_id=${row.taskId}&page_size=1000`, preferredOrg), {
                    credentials: 'include',
                    headers: buildHeaders(),
                });
                if (!jobsResp.ok) throw new Error(await jobsResp.text());
                const jobsData = await jobsResp.json();
                console.log('Task jobs:', jobsData);

                const existingQC = (jobsData.results || []).find((j: any) => j.type === 'quality_check');
                if (existingQC) {
                    setRows((prev) =>
                        prev.map((r) =>
                            r.key === row.key
                                ? { ...r, status: 'done', note: `QC job ${existingQC.id} already exists` }
                                : r,
                        ),
                    );
                    continue; // Skip creating a new one if there is already a qc job
                }

                let hasAnnotations = false;

                for (const j of jobsData.results || []) {
                    if (j.type === 'annotation' || j.type === 'default') {
                        try {
                            const metaResp = await fetch(withOrg(`/api/jobs/${j.id}/data/meta`, preferredOrg), {
                                credentials: 'include',
                                headers: buildHeaders(preferredOrg ? { 'X-Organization': preferredOrg } : undefined),
                            });
                            if (!metaResp.ok) continue;

                            const meta = await metaResp.json();
                            if ((meta.object_count || 0) > 0) {
                                hasAnnotations = true;
                                break;
                            }
                        } catch (err) {
                            console.warn(`Failed to check job ${j.id} annotations`, err);
                        }
                    }
                }

                if (!hasAnnotations) {
                    setRows((prev) =>
                        prev.map((r) =>
                            r.key === row.key
                                ? { ...r, status: 'waiting', note: 'No annotations found — QC skipped' }
                                : r,
                        ),
                    );
                    continue;
                }

                const numImages = task.size || 0;
                const qcCount = Math.max(1, Math.round(numImages * 0.1));

                const res = await fetch(withOrg('/api/jobs', preferredOrg), {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        ...buildHeaders(preferredOrg ? { 'X-Organization': preferredOrg } : undefined),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        task_id: row.taskId,
                        type: 'quality_check',
                        frame_selection_method: 'random_uniform',
                        frame_count: qcCount,
                        seed: Math.floor(Math.random() * 100000),
                    }),
                });

                const text = await res.text();
                if (!res.ok) throw new Error(text || `QC job creation failed (HTTP ${res.status})`);

                let qcJob: any = null;
                try {
                    qcJob = text ? JSON.parse(text) : null;
                } catch {}

                setRows((prev) =>
                    prev.map((r) =>
                        r.key === row.key
                            ? {
                                  ...r,
                                  status: 'done',
                                  note: qcJob?.id ? `QC job ${qcJob.id} created` : 'QC job created',
                              }
                            : r,
                    ),
                );
            } catch (e: any) {
                setRows((prev) =>
                    prev.map((r) => (r.key === row.key ? { ...r, note: `QC creation failed: ${e?.message || e}` } : r)),
                );
            }
        }

        message.success('QC job creation finished');
    }

    const uploadData = async (
        taskId: number,
        images: File[],
        imageQuality_: number,
        fieldName: 'client_files[]' | 'image',
        onProgress: (n: number) => void,
        ctxOrg?: string,
    ) => {
        const fd = new FormData();

        images.forEach((img, idx) => {
            if (fieldName === 'client_files[]') {
                fd.append(`client_files[${idx}]`, img, img.name);
            } else {
                fd.append('image', img, img.name);
            }
        });

        fd.append('image_quality', String(Math.max(5, Math.min(100, imageQuality_))));
        fd.append('type', 'images');
        fd.append('use_cache', 'false');
        fd.append('sorting_method', 'lexicographical');

        onProgress?.(5);

        const res = await fetch(withOrg(`/api/tasks/${taskId}/data`, ctxOrg), {
            method: 'POST',
            body: fd,
            credentials: 'include',
            headers: buildHeaders(ctxOrg ? { 'X-Organization': ctxOrg } : undefined),
        });

        if (!res.ok) {
            const txt = await res.text().catch(() => '');
            throw new Error(`Upload data failed (${res.status}) ${txt}`);
        }

        onProgress?.(100);
    };

    const uploadDataWithFallback = async (
        taskId: number,
        images: File[],
        imageQuality_: number,
        onProgress: (n: number) => void,
        ctxOrg?: string,
    ) => {
        try {
            await uploadData(taskId, images, imageQuality_, 'client_files[]', onProgress, ctxOrg);
        } catch (e: any) {
            const msg = String(e?.message || e);
            if (/failed.*\(400|415\)/i.test(msg) || /client_files/i.test(msg)) {
                onProgress?.(0);
                await uploadData(taskId, images, imageQuality_, 'image', onProgress, ctxOrg);
            } else {
                throw e;
            }
        }
    };

    // wait until the task reports frames > 0 (Takes into account delay to create task before uploading annotations)
    async function waitUntilTaskHasData(taskId: number, ctxOrg?: string, timeoutMs = 60000) {
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
            const r = await fetch(withOrg(`/api/tasks/${taskId}`, ctxOrg), {
                credentials: 'include',
                headers: buildHeaders(ctxOrg ? { 'X-Organization': ctxOrg } : undefined),
            });
            if (!r.ok) break;
            const t = await r.json();
            if ((t?.size || 0) > 0) return;
            await new Promise((r) => setTimeout(r, 1000));
        }
    }

    async function importAnnotations(
        taskId: number,
        file: File,
        patch?: (p: Partial<Row>) => void,
        ctxOrg?: string,
        skipPolling = false, // true = QC path, just return rq_id
    ): Promise<string | null> {
        if (!file || file.size === 0) throw new Error('Empty annotation file');

        const endpoint = `/api/tasks/${taskId}/annotations/`;
        const base = withOrg(endpoint, ctxOrg);
        const headers = buildHeaders();

        const startQS = new URLSearchParams({
            format: ANNOTATION_FORMAT,
            filename: file.name,
        }).toString();

        {
            const r = await fetch(appendQS(base, startQS), {
                method: 'POST',
                headers: { ...headers, 'Upload-Start': '' },
                credentials: 'include',
            });
            if (r.status !== 202) {
                throw new Error(`Stage annotations failed (${r.status}) ${await r.text()}`);
            }
        }
        let serverFilename = file.name;
        await new Promise<void>((resolve, reject) => {
            const upload = new TusUpload(file, {
                endpoint: base,
                metadata: { filename: file.name },
                headers: { ...headers },
                withCredentials: true,
                chunkSize: 5 * 1024 * 1024,
                retryDelays: [0, 500, 1000, 3000, 5000],
                storeFingerprintForResuming: false,
                onProgress: (sent, total) => {
                    const pct = total ? Math.max(1, Math.round((sent / total) * 100)) : 0;
                    patch?.({ progress: pct, note: `Uploading annotations… ${pct}%` });
                },
                onAfterResponse: (_req, res) => {
                    try {
                        const uf =
                            (res as any).getHeader?.('Upload-Filename') ??
                            (res as any).getResponseHeader?.('Upload-Filename');
                        if (uf) serverFilename = uf;
                    } catch {}
                    return res;
                },
                onSuccess: () => resolve(),
                onError: (err) => reject(err),
            });
            upload.start();
        });
        let rq_id: string | null = null;
        {
            const finishQS = new URLSearchParams({
                format: ANNOTATION_FORMAT,
                filename: serverFilename,
            }).toString();

            const r = await fetch(appendQS(base, finishQS), {
                method: 'POST',
                headers: { ...headers, 'Upload-Finish': '' },
                credentials: 'include',
            });
            const body = await r.text().catch(() => '');
            if (r.status !== 202) throw new Error(`Finish annotations failed (${r.status}) ${body}`);

            try {
                const parsed = JSON.parse(body);
                rq_id = parsed.rq_id;
            } catch {
                console.warn('Could not parse rq_id from finish response:', body);
            }
        }
        if (rq_id && !skipPolling) {
            patch?.({ status: 'done', note: 'Annotations imported', progress: 100 });
        }

        return rq_id;
    }

    async function waitUntilImportFinished(rq_id: string, ctxOrg?: string, timeoutMs = 600000) {
        const headers = buildHeaders();
        const rqUrl = withOrg(`/api/requests/${rq_id}`, ctxOrg);

        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
            const r = await fetch(rqUrl, { headers, credentials: 'include' });
            if (!r.ok) throw new Error(await r.text());
            const j = await r.json();
            if (j.state === 'finished') return;
            if (j.state === 'failed') throw new Error(j.message || 'Annotation import failed');
            await new Promise((res) => setTimeout(res, 1500));
        }
        throw new Error('Timed out waiting for annotation import');
    }

    /* --------------------------------- table --------------------------------- */

    const columns = [
        { title: 'Task', dataIndex: 'taskName', key: 'taskName' },
        { title: 'Raw images', dataIndex: 'rawCount', key: 'rawCount' },
        {
            title: 'Annotations',
            key: 'ann',
            render: (_: any, rec: Row) =>
                rec.hasLabelZip ? (
                    <Tag>label16.zip</Tag>
                ) : (
                    <span>{rec.labelCount ? `${rec.labelCount} files` : '—'}</span>
                ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, rec: Row) => {
                const color =
                    rec.status === 'done'
                        ? 'green'
                        : rec.status === 'error' || rec.status === 'error_duplicate'
                          ? 'red'
                          : rec.status === 'waiting'
                            ? 'orange'
                            : rec.status === 'skipped'
                              ? 'default'
                              : 'blue';
                return (
                    <Space>
                        <Tag color={color}>{rec.status}</Tag>
                        {(rec.status === 'uploading' || rec.status === 'importing') &&
                        typeof rec.progress === 'number' ? (
                            <Progress percent={rec.progress} size='small' style={{ width: 140 }} />
                        ) : null}
                        {rec.note ? <span style={{ opacity: 0.75 }}>{rec.note}</span> : null}
                    </Space>
                );
            },
        },
        { title: 'Task ID', dataIndex: 'taskId', key: 'taskId' },
    ];

    const fileMapRef = React.useRef<Map<string, File>>(new Map());
    const scheduleFlushRef = React.useRef<number | null>(null);

    function flushFilesSoon() {
        if (scheduleFlushRef.current != null) return;
        // batch updates to avoid 1000s of setState calls
        scheduleFlushRef.current = window.setTimeout(() => {
            scheduleFlushRef.current = null;
            setFiles(Array.from(fileMapRef.current.values()));
        }, 50);
    }

    const uploadProps: UploadProps = {
        directory: true,
        multiple: true,
        showUploadList: false,
        beforeUpload: (f) => {
            const file = f as unknown as File;
            const key = (file as any).webkitRelativePath || file.name;
            const m = fileMapRef.current;
            if (!m.has(key)) {
                m.set(key, file);
                flushFilesSoon();
            }
            return false;
        },
        onDrop() {
            // nothing needed
        },
        itemRender: () => null,
    };

    /* --------------------------------- runner -------------------------------- */

    async function runOne(taskName: string, g: Group, patch: (p: Partial<Row>) => void) {
        const ctxOrg = preferredOrg;

        // If project clearly belongs to an org but we failed to resolve its slug, stop early.
        if (
            selectedProject &&
            selectedProject.organizationSlug === undefined &&
            selectedProject.organizationName !== 'Personal'
        ) {
            patch({
                status: 'error',
                note: 'Cannot resolve project organization. Reload orgs or switch to the org in the header.',
            });
            return;
        }

        if (!g.raw.length) {
            patch({ status: 'skipped', note: 'No raw/ images' });
            return;
        }
        try {
            patch({ status: 'creating', note: '' });
            console.log('Creating task', taskName, 'in org:', ctxOrg ?? '(Personal)', 'projectId:', projectId);
            const id = await createTask(taskName, projectId!, ctxOrg);
            patch({ taskId: id });

            patch({ status: 'uploading', progress: 0 });
            await uploadDataWithFallback(id, g.raw, imageQuality, (pct) => patch({ progress: pct }), ctxOrg);
            await waitUntilTaskHasData(id, ctxOrg);

            if (g.label16Zip || g.label16Files.length) {
                patch({
                    status: 'importing',
                    progress: 0,
                    note: g.label16Zip ? 'Using label16.zip' : 'Zipping label16…',
                });

                // zip with progress if needed
                const file =
                    g.label16Zip ||
                    (await zipLabel16(g.label16Files, (pct) => {
                        patch({ progress: Math.max(1, pct), note: `Zipping label16… ${pct}%` });
                    }));
                await importAnnotations(id, file as File, patch, ctxOrg, false);
                return;
            }

            patch({ status: 'done', progress: 100, note: '' });
        } catch (e: any) {
            console.error(`[${taskName}]`, e);
            patch({ status: 'error', note: e?.message || String(e) });
        }
    }

    async function runAll() {
        if (!projectId) return message.error('Select a project first');

        // Only (re)run tasks that actually need work
        const runnableKeys = rows
            .filter((r) => r.status === 'queued' || r.status === 'error' || r.status === 'skipped')
            .filter((r) => r.status !== 'error_duplicate')
            .filter((r) => r.rawCount > 0 && r.status !== 'skipped') // don't run skipped/no-raw rows
            .map((r) => r.key);

        if (runnableKeys.length === 0) {
            return message.info('Nothing to do: all tasks are done or in progress.');
        }

        setRunning(true);
        let cursor = 0;

        const workers = new Array(Math.max(1, Math.min(concurrency, runnableKeys.length))).fill(0).map(async () => {
            while (cursor < runnableKeys.length) {
                const name = runnableKeys[cursor++];
                const g = grouped[name];
                const patch = (p: Partial<Row>) =>
                    setRows((prev) => prev.map((r) => (r.key === name ? { ...r, ...p } : r)));
                await runOne(name, g, patch);
            }
        });

        await Promise.all(workers);
        setRunning(false);
        message.success('Bulk creation finished');
    }

    /* --------------------------------- UI -------------------------------- */

    return (
        <div style={{ padding: 16 }}>
            <Typography.Title level={3}>Bulk Task Loader</Typography.Title>

            {loadError ? <Alert type='error' message={loadError} style={{ marginBottom: 12 }} /> : null}

            <Space wrap style={{ marginBottom: 12 }}>
                <span>Project:</span>
                <Select
                    style={{ width: 280 }}
                    placeholder='Select project'
                    value={projectId}
                    onChange={setProjectId}
                    options={projects.map((p) => ({
                        value: p.id,
                        label: `${p.name} — [${p.organizationName ?? (p.organizationSlug ? p.organizationSlug : 'Personal')}]`,
                    }))}
                />
                <span>Image quality:</span>
                <InputNumber
                    min={1}
                    max={100}
                    value={imageQuality}
                    onChange={(v) => setImageQuality(Number(v || 100))}
                />
                <span>Concurrency:</span>
                <InputNumber min={1} max={6} value={concurrency} onChange={(v) => setConcurrency(Number(v || 2))} />
                <Button
                    icon={<ClearOutlined />}
                    onClick={() => {
                        setFiles([]);
                        setRows([]);
                    }}
                >
                    Clear
                </Button>
            </Space>

            <Upload.Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                </p>
                <p className='ant-upload-text'>Drop your dataset root folder(s) here</p>
                <p className='ant-upload-hint'>
                    Expected per task: <code>TASK_NAME/raw/**</code> (images) and optional{' '}
                    <code>TASK_NAME/label16/**</code> (zip or files).
                </p>
            </Upload.Dragger>

            <div style={{ marginBottom: 8 }}>
                <Button type='primary' icon={<PlayCircleOutlined />} onClick={runAll} disabled={running}>
                    {running ? 'Running…' : 'Create Tasks'}
                </Button>
            </div>
            <div style={{ marginBottom: 8 }}>
                <Button type='default' onClick={createQCJobsForFinished} disabled={running}>
                    Create QC Jobs for Finished Tasks
                </Button>
            </div>

            <Table<Row> columns={columns as any} dataSource={rows} pagination={false} />
        </div>
    );
}
